export const CHARACTER_APPEARANCE_SCHEMA_VERSION = 3 as const;
export const PLAYER_APPEARANCE_SAVE_KEY = "vilnius.player-appearance.v3";
export const LEGACY_V2_PLAYER_APPEARANCE_SAVE_KEY =
  "vilnius.player-appearance.v2";
export const LEGACY_PLAYER_APPEARANCE_SAVE_KEY =
  "vilnius.player-appearance.v1";

export const APPEARANCE_OPTIONS = Object.freeze({
  faceShapes: [
    { id: "round", label: "Round" },
    { id: "oval", label: "Oval" },
    { id: "angular", label: "Angular" },
  ],
  bodyShapes: [
    { id: "slender", label: "Slender" },
    { id: "balanced", label: "Balanced" },
    { id: "broad", label: "Broad" },
  ],
  statures: [
    { id: "short", label: "Short" },
    { id: "average", label: "Average" },
    { id: "tall", label: "Tall" },
  ],
  ageGroups: [
    { id: "young_adult", label: "Young adult" },
    { id: "adult", label: "Adult" },
    { id: "older_adult", label: "Older adult" },
  ],
  skinTones: [
    { id: "porcelain", label: "Porcelain" },
    { id: "fair", label: "Fair" },
    { id: "warm", label: "Warm" },
    { id: "olive", label: "Olive" },
    { id: "deep", label: "Deep" },
  ],
  hairStyles: [
    { id: "bald", label: "Bare" },
    { id: "buzz_cut", label: "Buzz cut" },
    { id: "cropped", label: "Cropped" },
    { id: "undercut", label: "Undercut" },
    { id: "parted", label: "Side part" },
    { id: "bob", label: "Bob" },
    { id: "wavy", label: "Wavy" },
    { id: "ponytail", label: "Ponytail" },
    { id: "bun", label: "Bun" },
  ],
  facialHair: [
    { id: "none", label: "None" },
    { id: "stubble", label: "Stubble" },
    { id: "moustache", label: "Moustache" },
    { id: "short_beard", label: "Short beard" },
  ],
  hairColours: [
    { id: "flax", label: "Flax" },
    { id: "ash", label: "Ash brown" },
    { id: "chestnut", label: "Chestnut" },
    { id: "black", label: "Black" },
    { id: "auburn", label: "Auburn" },
    { id: "silver", label: "Silver" },
  ],
  eyeColours: [
    { id: "grey", label: "Grey" },
    { id: "blue", label: "Blue" },
    { id: "green", label: "Green" },
    { id: "hazel", label: "Hazel" },
    { id: "brown", label: "Brown" },
  ],
  outerwear: [
    { id: "wool_coat", label: "Wool coat" },
    { id: "field_jacket", label: "Field jacket" },
    { id: "cardigan", label: "Cardigan" },
    { id: "raincoat", label: "Raincoat" },
    { id: "track_jacket", label: "Track jacket" },
    { id: "suit_jacket", label: "Suit jacket" },
    { id: "hoodie", label: "Hoodie" },
    { id: "quilted_coat", label: "Quilted coat" },
    { id: "denim_jacket", label: "Denim jacket" },
  ],
  lowerGarments: [
    { id: "trousers", label: "Trousers" },
    { id: "wide_trousers", label: "Wide trousers" },
    { id: "track_pants", label: "Track pants" },
    { id: "skirt", label: "Skirt" },
  ],
  footwear: [
    { id: "walking_boots", label: "Walking boots" },
    { id: "trainers", label: "Trainers" },
    { id: "dress_shoes", label: "Dress shoes" },
    { id: "work_boots", label: "Work boots" },
  ],
  eyewear: [
    { id: "none", label: "None" },
    { id: "round_glasses", label: "Round glasses" },
    { id: "square_glasses", label: "Square glasses" },
  ],
  headwear: [
    { id: "none", label: "None" },
    { id: "knit_cap", label: "Knit cap" },
    { id: "flat_cap", label: "Flat cap" },
    { id: "baseball_cap", label: "Baseball cap" },
    { id: "cycle_helmet", label: "Cycle helmet" },
    { id: "hard_hat", label: "Hard hat" },
    { id: "beret", label: "Beret" },
    { id: "headscarf", label: "Headscarf" },
  ],
  neckwear: [
    { id: "none", label: "None" },
    { id: "scarf", label: "Scarf" },
    { id: "kerchief", label: "Kerchief" },
    { id: "tie", label: "Tie" },
    { id: "lanyard", label: "Lanyard" },
    { id: "chain", label: "Chain" },
  ],
  carriedAccessories: [
    { id: "none", label: "None" },
    { id: "satchel", label: "Satchel" },
    { id: "tote", label: "Tote bag" },
    { id: "backpack", label: "Backpack" },
    { id: "briefcase", label: "Briefcase" },
    { id: "shopping_bag", label: "Shopping bag" },
    { id: "cane", label: "Walking cane" },
    { id: "folio", label: "Book or laptop folio" },
    { id: "camera", label: "Compact camera" },
    { id: "produce_basket", label: "Produce basket" },
    { id: "tool_roll", label: "Tool roll" },
    { id: "stroller", label: "Stroller" },
    { id: "dog_lead", label: "Dog and lead" },
    { id: "waist_pack", label: "Waist pack" },
  ],
} as const);

type OptionId<T extends readonly Readonly<{ id: string }>[]> = T[number]["id"];

export type FaceShape = OptionId<typeof APPEARANCE_OPTIONS.faceShapes>;
export type BodyShape = OptionId<typeof APPEARANCE_OPTIONS.bodyShapes>;
export type CharacterStature = OptionId<typeof APPEARANCE_OPTIONS.statures>;
export type CharacterAgeGroup = OptionId<
  typeof APPEARANCE_OPTIONS.ageGroups
>;
export type SkinTone = OptionId<typeof APPEARANCE_OPTIONS.skinTones>;
export type HairStyle = OptionId<typeof APPEARANCE_OPTIONS.hairStyles>;
export type HairColour = OptionId<typeof APPEARANCE_OPTIONS.hairColours>;
export type FacialHair = OptionId<typeof APPEARANCE_OPTIONS.facialHair>;
export type EyeColour = OptionId<typeof APPEARANCE_OPTIONS.eyeColours>;
export type Outerwear = OptionId<typeof APPEARANCE_OPTIONS.outerwear>;
export type LowerGarment = OptionId<typeof APPEARANCE_OPTIONS.lowerGarments>;
export type Footwear = OptionId<typeof APPEARANCE_OPTIONS.footwear>;
export type Eyewear = OptionId<typeof APPEARANCE_OPTIONS.eyewear>;
export type Headwear = OptionId<typeof APPEARANCE_OPTIONS.headwear>;
export type Neckwear = OptionId<typeof APPEARANCE_OPTIONS.neckwear>;
export type CarriedAccessory = OptionId<
  typeof APPEARANCE_OPTIONS.carriedAccessories
>;

export interface CharacterAppearance {
  readonly schemaVersion: typeof CHARACTER_APPEARANCE_SCHEMA_VERSION;
  readonly faceShape: FaceShape;
  readonly bodyShape: BodyShape;
  readonly stature: CharacterStature;
  readonly ageGroup: CharacterAgeGroup;
  readonly skinTone: SkinTone;
  readonly hairStyle: HairStyle;
  readonly hairColour: HairColour;
  readonly facialHair: FacialHair;
  readonly eyeColour: EyeColour;
  readonly outerwear: Outerwear;
  readonly lowerGarment: LowerGarment;
  readonly footwear: Footwear;
  readonly eyewear: Eyewear;
  readonly headwear: Headwear;
  readonly neckwear: Neckwear;
  readonly carriedAccessory: CarriedAccessory;
  readonly colours: {
    readonly outerwear: number;
    readonly lower: number;
    readonly accent: number;
  };
}

export type CharacterAppearanceOverrides = Partial<
  Omit<CharacterAppearance, "schemaVersion" | "colours">
> & {
  readonly colours?: Partial<CharacterAppearance["colours"]>;
};

const OUTERWEAR_PALETTE = [
  0x6f493b,
  0x59665c,
  0x4f5d68,
  0x66535f,
  0x776547,
  0x485b58,
] as const;
const LOWER_PALETTE = [
  0x303839,
  0x40423d,
  0x45404a,
  0x3e4850,
  0x51483f,
] as const;
const ACCENT_PALETTE = [
  0xc29668,
  0x9d6f65,
  0x82979b,
  0xb1a174,
  0x7e8e6c,
  0x9a7c9f,
] as const;

export const DEFAULT_PLAYER_APPEARANCE: CharacterAppearance = Object.freeze({
  schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
  faceShape: "angular",
  bodyShape: "balanced",
  stature: "average",
  ageGroup: "adult",
  skinTone: "warm",
  hairStyle: "parted",
  hairColour: "chestnut",
  facialHair: "none",
  eyeColour: "grey",
  outerwear: "wool_coat",
  lowerGarment: "trousers",
  footwear: "walking_boots",
  eyewear: "none",
  headwear: "none",
  neckwear: "scarf",
  carriedAccessory: "none",
  colours: Object.freeze({
    outerwear: 0x7f553d,
    lower: 0x343b3b,
    accent: 0xb88959,
  }),
});

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function pick<T>(values: readonly T[], seed: string): T {
  const value = values[stableHash(seed) % values.length];
  if (value === undefined) {
    throw new Error("Appearance option list must not be empty");
  }
  return value;
}

function optionId<T extends readonly Readonly<{ id: string }>[]>(
  values: T,
  seed: string,
): OptionId<T> {
  return pick(values, seed).id as OptionId<T>;
}

function sparseOptionId<
  T extends readonly Readonly<{ id: string } & ({ id: "none" } | object)>[],
>(
  values: T,
  seed: string,
  presencePercent: number,
): OptionId<T> {
  if (stableHash(seed + ":presence") % 100 >= presencePercent) {
    return "none" as OptionId<T>;
  }
  const presentValues = values.filter((option) => option.id !== "none");
  return pick(presentValues, seed + ":style").id as OptionId<T>;
}

export function generateCharacterAppearance(
  stableId: string,
  overrides: Readonly<CharacterAppearanceOverrides> = {},
): CharacterAppearance {
  if (stableId.length === 0) {
    throw new Error("Character appearance requires a stable ID");
  }
  const generatedHeadwear = sparseOptionId(
    APPEARANCE_OPTIONS.headwear,
    stableId + ":headwear",
    25,
  );
  const generatedHairStyle = optionId(
    APPEARANCE_OPTIONS.hairStyles,
    stableId + ":hair-style",
  );
  const generatedHighVolumeHair = generatedHairStyle === "bun" ||
    generatedHairStyle === "ponytail" || generatedHairStyle === "wavy";
  const generated: CharacterAppearance = {
    schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
    faceShape: optionId(APPEARANCE_OPTIONS.faceShapes, stableId + ":face"),
    bodyShape: optionId(APPEARANCE_OPTIONS.bodyShapes, stableId + ":body"),
    stature: optionId(APPEARANCE_OPTIONS.statures, stableId + ":stature"),
    ageGroup: pick(
      ["young_adult", "adult", "adult", "adult", "older_adult"] as const,
      stableId + ":age-group",
    ),
    skinTone: optionId(APPEARANCE_OPTIONS.skinTones, stableId + ":skin"),
    hairStyle: generatedHeadwear !== "none" && generatedHighVolumeHair
      ? "cropped"
      : generatedHairStyle,
    hairColour: optionId(
      APPEARANCE_OPTIONS.hairColours,
      stableId + ":hair-colour",
    ),
    facialHair: sparseOptionId(
      APPEARANCE_OPTIONS.facialHair,
      stableId + ":facial-hair",
      28,
    ),
    eyeColour: optionId(APPEARANCE_OPTIONS.eyeColours, stableId + ":eyes"),
    outerwear: optionId(APPEARANCE_OPTIONS.outerwear, stableId + ":outerwear"),
    lowerGarment: optionId(
      APPEARANCE_OPTIONS.lowerGarments,
      stableId + ":lower-garment",
    ),
    footwear: optionId(APPEARANCE_OPTIONS.footwear, stableId + ":footwear"),
    eyewear: sparseOptionId(
      APPEARANCE_OPTIONS.eyewear,
      stableId + ":eyewear",
      24,
    ),
    headwear: generatedHeadwear,
    neckwear: sparseOptionId(
      APPEARANCE_OPTIONS.neckwear,
      stableId + ":neckwear",
      34,
    ),
    carriedAccessory: sparseOptionId(
      APPEARANCE_OPTIONS.carriedAccessories,
      stableId + ":carried",
      28,
    ),
    colours: {
      outerwear: pick(OUTERWEAR_PALETTE, stableId + ":outer-colour"),
      lower: pick(LOWER_PALETTE, stableId + ":lower-colour"),
      accent: pick(ACCENT_PALETTE, stableId + ":accent-colour"),
    },
  };
  return freezeCharacterAppearance({
    ...generated,
    ...overrides,
    schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
    colours: {
      ...generated.colours,
      ...overrides.colours,
    },
  });
}

export function resolvePresentedHairStyle(
  appearance: Readonly<CharacterAppearance>,
): HairStyle {
  if (appearance.headwear === "headscarf") {
    return "bald";
  }
  const highVolumeHair = appearance.hairStyle === "bun" ||
    appearance.hairStyle === "ponytail" || appearance.hairStyle === "wavy";
  return appearance.headwear !== "none" && highVolumeHair
    ? "cropped"
    : appearance.hairStyle;
}

export function serializePlayerAppearance(
  appearance: Readonly<CharacterAppearance>,
): string {
  validateCharacterAppearance(appearance);
  return JSON.stringify(appearance);
}

export function parsePlayerAppearance(value: string): CharacterAppearance {
  const parsed: unknown = JSON.parse(value);
  if (
    typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) &&
    ((parsed as Record<string, unknown>).schemaVersion === 1 ||
      (parsed as Record<string, unknown>).schemaVersion === 2)
  ) {
    const schemaVersion = (parsed as Record<string, unknown>).schemaVersion;
    const migrated = {
      ...(parsed as Record<string, unknown>),
      schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
      ...(schemaVersion === 1
        ? { ageGroup: "adult", facialHair: "none" }
        : {}),
      footwear: "walking_boots",
    };
    validateCharacterAppearance(migrated);
    return freezeCharacterAppearance(
      structuredClone(migrated) as CharacterAppearance,
    );
  }
  validateCharacterAppearance(parsed);
  return freezeCharacterAppearance(structuredClone(parsed));
}

export function validateCharacterAppearance(
  value: unknown,
): asserts value is CharacterAppearance {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Character appearance must be an object");
  }
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== CHARACTER_APPEARANCE_SCHEMA_VERSION) {
    throw new Error("Unsupported character appearance schemaVersion");
  }
  assertOption(record.faceShape, APPEARANCE_OPTIONS.faceShapes, "face shape");
  assertOption(record.bodyShape, APPEARANCE_OPTIONS.bodyShapes, "body shape");
  assertOption(record.stature, APPEARANCE_OPTIONS.statures, "stature");
  assertOption(record.ageGroup, APPEARANCE_OPTIONS.ageGroups, "age group");
  assertOption(record.skinTone, APPEARANCE_OPTIONS.skinTones, "skin tone");
  assertOption(record.hairStyle, APPEARANCE_OPTIONS.hairStyles, "hair style");
  assertOption(record.hairColour, APPEARANCE_OPTIONS.hairColours, "hair colour");
  assertOption(record.facialHair, APPEARANCE_OPTIONS.facialHair, "facial hair");
  assertOption(record.eyeColour, APPEARANCE_OPTIONS.eyeColours, "eye colour");
  assertOption(record.outerwear, APPEARANCE_OPTIONS.outerwear, "outerwear");
  assertOption(
    record.lowerGarment,
    APPEARANCE_OPTIONS.lowerGarments,
    "lower garment",
  );
  assertOption(record.footwear, APPEARANCE_OPTIONS.footwear, "footwear");
  assertOption(record.eyewear, APPEARANCE_OPTIONS.eyewear, "eyewear");
  assertOption(record.headwear, APPEARANCE_OPTIONS.headwear, "headwear");
  assertOption(record.neckwear, APPEARANCE_OPTIONS.neckwear, "neckwear");
  assertOption(
    record.carriedAccessory,
    APPEARANCE_OPTIONS.carriedAccessories,
    "carried accessory",
  );
  if (
    typeof record.colours !== "object" ||
    record.colours === null ||
    Array.isArray(record.colours)
  ) {
    throw new Error("Character appearance colours must be an object");
  }
  const colours = record.colours as Record<string, unknown>;
  for (const key of ["outerwear", "lower", "accent"] as const) {
    const colour = colours[key];
    if (
      typeof colour !== "number" ||
      !Number.isInteger(colour) ||
      colour < 0 ||
      colour > 0xff_ffff
    ) {
      throw new Error("Character appearance has invalid " + key + " colour");
    }
  }
}

function freezeCharacterAppearance(
  appearance: CharacterAppearance,
): CharacterAppearance {
  Object.freeze(appearance.colours);
  return Object.freeze(appearance);
}

function assertOption<T extends readonly Readonly<{ id: string }>[]>(
  value: unknown,
  options: T,
  label: string,
): asserts value is OptionId<T> {
  if (
    typeof value !== "string" ||
    !options.some((option) => option.id === value)
  ) {
    throw new Error("Character appearance has invalid " + label);
  }
}
